import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { TextInput } from 'Views/Components/New/TextInput';
import { getMessageByKey } from 'Stores/LanguageStore';
import {
	getVisibleDynamicFields,
	useDynamicBookingFieldsStore,
	CachedDynamicField,
} from 'Stores/DynamicBookingFieldsStore';
import { useDynamicBookingSectionsStore } from 'Stores/DynamicBookingSectionsStore';
import type { DynamicFieldBookingType } from 'Modules/DynamicBookingFields/Types/DynamicBookingFieldFormSchema';
import { IsValidPhone } from 'Validators/Functions/HumanWritten/Phone';
import { isEmail } from 'Validators/Functions/Email';
import DynamicFieldRenderer from './DynamicFieldRenderer';

/**
 * Configures the original booking "note" ("Additional information") field so it
 * renders inside this section alongside the dynamic fields. Enabled/required and
 * the resolved label/placeholder are owned by the caller (they differ between the
 * walk-on `passengerNote*` and vehicle `vehicleNote*` app-config keys).
 */
export interface DynamicFieldsNoteConfig {
	enabled: boolean;
	label: string;
	placeholder?: string;
	required?: boolean;
	value: string;
	error?: string;
	onChange: (next: string) => void;
}

interface Props {
	/**
	 * Which booking flow is being rendered. Ignored when `fields` is supplied — a charter has no
	 * booking type, so it hands the list over instead of naming one.
	 */
	bookingType?: DynamicFieldBookingType;
	subjectUserDisplayNameId?: string | null;
	/**
	 * The fields to render, chosen by the caller. Supplied by anything that is not a booking flow —
	 * a charter picks its own set with `getVisibleCharterFields` — so the grouping, the inputs and
	 * the validation below stay one implementation rather than two.
	 */
	fields?: CachedDynamicField[];
	values: Record<string, string>;
	errors?: Record<string, string>;
	onChange: (next: Record<string, string>) => void;
	readOnly?: boolean;
	/**
	 * When false, renders just the field grid without the surrounding section
	 * card. Default true — the walk-on, Vehicle and Freight tabs all want the
	 * "Additional information" header styling.
	 */
	wrapInCard?: boolean;
	/**
	 * The original free-text booking note, rendered full-width below the dynamic
	 * fields. When supplied and enabled the section renders even with no dynamic
	 * fields so the note still appears in the "Additional information" area.
	 */
	noteField?: DynamicFieldsNoteConfig;
}

/**
 * Renders the active dynamic fields for the given booking type, plus the original
 * free-text booking note when `noteField` is enabled. Shared between the walk-on
 * BookingDetailsTab and the Vehicle/Freight tabs' appended sections. Returns null
 * when there are neither visible fields nor an enabled note so callers can skip
 * rendering empty containers.
 */
const DynamicFieldsSection = observer(
	({
		bookingType,
		subjectUserDisplayNameId,
		fields: fieldsOverride,
		values,
		errors,
		onChange,
		readOnly,
		wrapInCard = true,
		noteField,
	}: Props) => {
		const { fields, loaded, loadFields } = useDynamicBookingFieldsStore();
		const { sections, loaded: sectionsLoaded, loadSections } = useDynamicBookingSectionsStore();

		useEffect(() => {
			if (!loaded) loadFields();
		}, [loaded, loadFields]);
		useEffect(() => {
			if (!sectionsLoaded) loadSections();
		}, [sectionsLoaded, loadSections]);

		const visible =
			fieldsOverride ??
			(bookingType ? getVisibleDynamicFields(fields, bookingType, subjectUserDisplayNameId) : []);

		// TOGGLE fields render unchecked when no value is stored, so seed missing values
		// to 'false' — the unchecked state is a valid "No" and a required toggle must not
		// force the user to check/uncheck it before they can continue. Also persists the
		// explicit answer on the booking.
		useEffect(() => {
			if (readOnly) return;
			const missingToggles = visible.filter(f => {
				const raw = values?.[f.id];
				return f.fieldType === 'TOGGLE' && (raw == null || raw === '');
			});
			if (missingToggles.length === 0) return;
			const next = { ...(values || {}) };
			missingToggles.forEach(f => {
				next[f.id] = 'false';
			});
			onChange(next);
		});
		const showNote = noteField?.enabled ?? false;
		// Render nothing when there are neither dynamic fields nor an enabled note so
		// callers can skip rendering empty containers.
		if (visible.length === 0 && !showNote) return null;

		const setOne = (field: CachedDynamicField, next: string) => {
			onChange({ ...(values || {}), [field.id]: next });
		};

		// Grid of inputs for a given set of fields. The note (when enabled) is appended
		// to whichever grid hosts it — always the default "Additional information" card.
		const buildGrid = (gridFields: CachedDynamicField[], includeNote: boolean) => (
			<div className="dynamic-fields-section grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
				{gridFields.map(field => (
					<div key={field.id} className={field.fieldType === 'TEXTAREA' ? 'md:col-span-2' : undefined}>
						<DynamicFieldRenderer
							field={field}
							value={(values && values[field.id]) ?? ''}
							onChange={next => setOne(field, next)}
							error={errors?.[field.id]}
							readOnly={readOnly}
						/>
					</div>
				))}
				{includeNote && showNote && noteField && (
					<TextInput
						className="md:col-span-2"
						id="note"
						label={noteField.label}
						placeholder={noteField.placeholder}
						required={noteField.required}
						value={noteField.value ?? ''}
						error={noteField.error}
						readOnly={readOnly}
						onChange={e => noteField.onChange(e.target.value)}
						data-test-id="booking-note"
					/>
				)}
			</div>
		);

		// Un-wrapped mode renders a single flat grid (no section cards) — used when a
		// caller embeds the fields inside its own container.
		if (!wrapInCard) return buildGrid(visible, true);

		// Group visible fields under their assigned, non-archived section. Sections with
		// no visible field are skipped; fields whose section is missing/archived (or that
		// have none) fall back to the default "Additional information" card.
		const activeSectionIds = new Set(sections.filter(s => !s.archived).map(s => s.id));
		const orderedSections = sections
			.filter(s => !s.archived)
			.filter(s => visible.some(f => f.sectionId === s.id))
			.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
		const unassigned = visible.filter(f => !f.sectionId || !activeSectionIds.has(f.sectionId));

		// Default "Additional information" copy is language-driven so it can be
		// translated per site, with the defaults living in default-translations.json.
		const defaultHeader = getMessageByKey(
			'booking-wizard__additional-information__header',
			'Additional information',
		);
		const defaultDescription = getMessageByKey(
			'booking-wizard__additional-information__description',
			'A few extra details we need for this booking.',
		);

		return (
			<>
				{orderedSections.map(section => {
					const sectionFields = visible.filter(f => f.sectionId === section.id);
					const header = section.labelKey
						? (getMessageByKey(section.labelKey, section.label) ?? section.label)
						: section.label;
					const description = section.descriptionKey
						? getMessageByKey(section.descriptionKey, section.description ?? '')
						: section.description;
					return (
						<section key={section.id} className="booking-section-card">
							<header className="booking-section-header">
								<span
									className="booking-section-heading__icon"
									style={
										section.colour
											? {
													color: section.colour,
													// Match the default square's 20% tint, but in the section's colour.
													backgroundColor: `color-mix(in srgb, ${section.colour} 20%, transparent)`,
												}
											: undefined
									}
								>
									<Icon name={section.icon || 'description'} useMaterialIcons />
								</span>
								<div className="booking-section-header__text">
									<h4 className="booking-section-title">{header}</h4>
									{description && <p className="booking-section-description">{description}</p>}
								</div>
							</header>
							{buildGrid(sectionFields, false)}
						</section>
					);
				})}
				{(unassigned.length > 0 || showNote) && (
					<section className="booking-section-card">
						<header className="booking-section-header">
							<span className="booking-section-heading__icon">
								<Icon name="description" useMaterialIcons />
							</span>
							<div className="booking-section-header__text">
								<h4 className="booking-section-title">{defaultHeader}</h4>
								{defaultDescription && (
									<p className="booking-section-description">{defaultDescription}</p>
								)}
							</div>
						</header>
						{buildGrid(unassigned, true)}
					</section>
				)}
			</>
		);
	},
);

export default DynamicFieldsSection;

/**
 * Validate that all required dynamic fields for the given booking type/customer type
 * have a non-empty value. Returns an error map keyed by field id; empty when OK.
 */
export function validateDynamicFields(
	fields: CachedDynamicField[],
	bookingType: DynamicFieldBookingType,
	subjectUserDisplayNameId: string | null | undefined,
	values: Record<string, string> | undefined,
	/**
	 * Which "required" flag gates the empty check. The booking wizard uses
	 * `required`; check-in uses `requiredInCheckIn`.
	 */
	requiredFlag: 'required' | 'requiredInCheckIn' = 'required',
): Record<string, string> {
	return validateDynamicFieldValues(
		getVisibleDynamicFields(fields, bookingType, subjectUserDisplayNameId),
		values,
		requiredFlag,
	);
}

/**
 * The same checks over a list the caller has already chosen — required answers present, and email
 * and phone answers in the right shape. Used by anything without a booking type to filter by, so a
 * charter is judged by the same rules as a booking rather than a second set that could drift.
 */
export function validateDynamicFieldValues(
	visible: CachedDynamicField[],
	values: Record<string, string> | undefined,
	requiredFlag: 'required' | 'requiredInCheckIn' = 'required',
): Record<string, string> {
	const errors: Record<string, string> = {};
	visible.forEach(f => {
		const raw = values?.[f.id];
		const isEmptyValue = raw == null || String(raw).trim() === '';

		if (f[requiredFlag] && isEmptyValue) {
			// An unset TOGGLE renders identically to an unchecked one ("No"), which is a
			// valid answer — the UI seeds missing toggle values to 'false', so a still-empty
			// value must not block progress.
			if (f.fieldType !== 'TOGGLE') {
				errors[f.id] = 'Required field';
			}
			return;
		}

		// Format validation only runs on non-empty values so optional fields can be
		// left blank; the required check above already covers the empty case.
		if (isEmptyValue) return;

		if (f.fieldType === 'PHONE' && !IsValidPhone(String(raw))) {
			errors[f.id] = 'Enter a valid phone number';
		} else if (f.fieldType === 'EMAIL' && !isEmail(String(raw))) {
			errors[f.id] = 'Enter a valid email';
		}
	});
	return errors;
}
